import React, { useState, useEffect, useCallback } from 'react';
import ErpiaSelect from './ErpiaSelect';
import APIService, { urlAvecJeton } from '../services/api';
// La MÊME modale que sur les profils du besoin — un seul rendu de la
// décomposition d'un score, quel que soit l'écran qui la demande.
import { ScoreDetailModal } from './MatchingReglages';

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const jsonH = () => ({ ...authH(), 'Content-Type': 'application/json' });

// Logo sourcia : carré sombre « so » mono + point rouge.
const SoMark = ({ size = 26 }) => (
  <span style={{ width: size, height: size, borderRadius: Math.round(size / 4), background: 'var(--text-strong)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', position: 'relative', flex: 'none' }}>
    <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: Math.round(size * 0.42), fontWeight: 600, color: 'var(--bg)' }}>so</span>
    <span style={{ position: 'absolute', bottom: Math.round(size * 0.18), right: Math.round(size * 0.18), width: Math.max(3, Math.round(size * 0.13)), height: Math.max(3, Math.round(size * 0.13)), borderRadius: 1, background: 'var(--brand)' }} />
  </span>
);

// Champs masquables : chaque champ peut être Publié ou Masqué (valeur de substitution).
// Le masquage réel est appliqué CÔTÉ SERVEUR — ici on ne fait que piloter les drapeaux.
export const MASK_DEFS = [
  { key: 'client',    label: 'Nom du client final',   masked: 'Client confidentiel' },
  { key: 'salary',    label: 'Salaire maximum',       masked: 'Sur demande' },
  { key: 'location',  label: 'Localisation précise',  masked: 'Suisse romande' },
  { key: 'start_date',label: 'Date de démarrage',     masked: 'À définir' },
  { key: 'owner',     label: 'Responsable du besoin', masked: 'Non communiqué' },
];
const DEFAULT_MASKS = { client: true, salary: false, location: false, start_date: false, owner: true };
const DEADLINES = [
  { value: '3d', label: '3 jours' },
  { value: '7d', label: '7 jours' },
  { value: '14d', label: '14 jours' },
  { value: 'none', label: 'Sans limite' },
];
const AUDIENCES_FALLBACK = [
  { id: 'all', name: 'Tous les sourceurs', count: null },
  { id: 'it', name: 'Spécialistes IT / Engineering', count: null },
  { id: 'romandie', name: 'Réseau Suisse romande', count: null },
];

// La réponse back résout response_deadline en timestamp ISO (date exacte de
// clôture) — c'est cette date qu'on affiche ; le raccourci (7d…) ne sert qu'à
// la saisie. null / 'none' = sans limite.
const deadlineLabel = (v) => {
  if (v == null || v === '' || v === 'none') return 'Sans limite';
  const known = DEADLINES.find(d => d.value === v);
  if (known) return known.label;
  const dt = new Date(v);
  if (!Number.isNaN(dt.getTime())) return `jusqu'au ${dt.toLocaleDateString('fr-FR')}`;
  return String(v);
};

// salary_max_annual peut revenir résolu en entier (ex. 165000) → réaffichage suisse.
const fmtSalary = (v) => {
  if (v == null || v === '') return '';
  const n = Number(String(v).replace(/[^\d.-]/g, ''));
  if (!Number.isFinite(n) || n <= 0) return String(v);
  return n.toLocaleString('fr-CH').replace(/\s/g, "'") + ' CHF/an';
};

// Salaire maximum ANNUEL suggéré depuis le tarif du besoin (jamais le TJM tel quel) :
// borne haute du TJM × ~218 jours ouvrés, arrondi aux 5'000 ; si déjà en K CHF/an, borne haute.
export const suggestSalaryAnnual = (tarif) => {
  const s = String(tarif || '');
  const nums = (s.match(/\d[\d'’\s]*/g) || []).map(x => Number(x.replace(/[^\d]/g, ''))).filter(Boolean);
  if (!nums.length) return '';
  const hi = Math.max(...nums);
  const annual = /an|année|\bk\b/i.test(s) ? (hi < 1000 ? hi * 1000 : hi) : Math.round((hi * 218) / 5000) * 5000;
  return annual.toLocaleString('fr-CH').replace(/\s/g, "'") + ' CHF/an';
};

const besoinField = (b, keys) => { for (const k of keys) { if (b?.[k] != null && b[k] !== '') return b[k]; } return ''; };

// ── Hook : état diffusion + propositions d'un besoin ─────────────────────────
// `enabled: false` (flag org coupé pour cet utilisateur) → AUCUN appel réseau :
// sinon chaque ouverture de besoin déclenche 3 × 403 feature_disabled → 3 toasts.
export function useDiffusion(besoin, { showNotification, onCandidatsChanged, enabled = true } = {}) {
  const besoinId = besoin?.id;
  const [diffusion, setDiffusion] = useState(null);   // null = non diffusé
  const [proposals, setProposals] = useState([]);
  const [audiences, setAudiences] = useState(AUDIENCES_FALLBACK);
  const [loading, setLoading] = useState(false);
  const [draftOpen, setDraftOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyPropId, setBusyPropId] = useState(null);

  const load = useCallback(async () => {
    if (besoinId == null || !enabled) return;
    setLoading(true);
    try {
      const [dRes, pRes, aRes] = await Promise.all([
        fetch(`${BASE}/besoins/${besoinId}/diffusion`, { headers: authH() }),
        fetch(`${BASE}/besoins/${besoinId}/propositions`, { headers: authH() }),
        fetch(`${BASE}/diffusion/audiences`, { headers: authH() }).catch(() => null),
      ]);
      // Diffusion : 404 = pas encore diffusé (état normal, pas une erreur)
      if (dRes.ok) { const j = await dRes.json().catch(() => ({})); setDiffusion(j.diffusion || j.data || (j.id != null ? j : null)); }
      else setDiffusion(null);
      if (pRes.ok) { const j = await pRes.json().catch(() => ({})); const arr = j.propositions || j.data || (Array.isArray(j) ? j : []); setProposals(Array.isArray(arr) ? arr : []); }
      if (aRes && aRes.ok) { const j = await aRes.json().catch(() => ({})); const arr = j.audiences || j.data || (Array.isArray(j) ? j : []); if (Array.isArray(arr) && arr.length) setAudiences(arr); }
    } catch { /* offline / non déployé → non diffusé, aucune proposition */ }
    finally { setLoading(false); }
  }, [besoinId, enabled]);

  useEffect(() => { load(); }, [load]);

  const publish = useCallback(async (draft) => {
    if (besoinId == null) return;
    setSaving(true);
    try {
      const existed = !!diffusion;
      const res = await fetch(`${BASE}/besoins/${besoinId}/diffusion`, {
        method: 'POST', headers: jsonH(),
        body: JSON.stringify({
          masked_fields: MASK_DEFS.filter(m => draft.masks[m.key]).map(m => m.key),
          // Seule une VRAIE liste serveur a un identifiant numérique. « Tous
          // les sourceurs » et les replis statiques ('all', 'it'…) n'existent
          // pas en base : les envoyer tels quels finissait en NaN dans une
          // colonne entière côté serveur. null = diffusion à tous, comme avant.
          audience_list_id: Number.isFinite(Number(draft.audienceId)) && String(draft.audienceId).trim() !== '' ? Number(draft.audienceId) : null,
          response_deadline: draft.deadline,
          salary_max_annual: draft.salaryMaxAnnual || null,
        }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j.success === false) throw new Error(j.error || `HTTP ${res.status}`);
      setDiffusion(j.diffusion || j.data || j);
      setDraftOpen(false);
      const aud = audiences.find(a => a.id === draft.audienceId);
      showNotification?.(existed ? 'Diffusion mise à jour' : `Besoin diffusé${aud?.count != null ? ` à ${aud.count} sourceurs` : ''}`, 'success');
    } catch (e) { showNotification?.('Erreur diffusion : ' + e.message, 'error'); }
    finally { setSaving(false); }
  }, [besoinId, diffusion, audiences, showNotification]);

  const unpublish = useCallback(async () => {
    if (besoinId == null) return;
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/besoins/${besoinId}/diffusion`, { method: 'DELETE', headers: authH() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDiffusion(null); setDraftOpen(false);
      showNotification?.('Diffusion retirée — les propositions reçues sont conservées', 'success');
    } catch (e) { showNotification?.('Erreur : ' + e.message, 'error'); }
    finally { setSaving(false); }
  }, [besoinId, showNotification]);

  const decide = useCallback(async (propId, action, reason) => {
    setBusyPropId(propId);
    try {
      // Refus : raison optionnelle transmise au sourceur ({ reason }) — ignorée par accept.
      const body = action === 'reject' && reason?.trim() ? JSON.stringify({ reason: reason.trim().slice(0, 500) }) : undefined;
      const res = await fetch(`${BASE}/propositions/${propId}/${action}`, { method: 'POST', headers: jsonH(), ...(body ? { body } : {}) });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j.success === false) throw new Error(j.error || `HTTP ${res.status}`);
      const newStatus = action === 'accept' ? 'accepted' : 'rejected';
      // Mise à jour optimiste : on privilégie la raison RENVOYÉE par le back
      // (bornée/normalisée à 500 caractères côté serveur) sur le texte saisi.
      const storedReason = j.rejection_reason ?? (reason?.trim() || null);
      // Traçabilité : qui a décidé, quand — valeurs back si présentes, sinon
      // l'utilisateur connecté + maintenant (le back fait foi au prochain GET).
      const me = APIService.getCurrentUser?.();
      const meName = me ? ([me.prenom, me.nom].filter(Boolean).join(' ').trim() || me.username || null) : null;
      const decidedBy = j.decided_by_name ?? j.rejected_by_name ?? j.accepted_by_name ?? meName;
      const decidedAt = j.decided_at ?? j.rejected_at ?? j.accepted_at ?? new Date().toISOString();
      setProposals(prev => prev.map(p => p.id === propId ? { ...p, status: newStatus, decided_by_name: decidedBy, decided_at: decidedAt, ...(action === 'reject' && storedReason ? { rejection_reason: storedReason } : {}) } : p));
      if (action === 'accept') { showNotification?.('Profil ajouté aux candidats — sourceur notifié', 'success'); onCandidatsChanged?.(); }
      else showNotification?.('Proposition refusée — sourceur notifié', 'success');
    } catch (e) { showNotification?.('Erreur : ' + e.message, 'error'); }
    finally { setBusyPropId(null); }
  }, [showNotification, onCandidatsChanged]);

  // ── Matching s9 du CV proposé ↔ le besoin (POST /propositions/:id/matching) ──
  const [scoringPropId, setScoringPropId] = useState(null); // scoring unitaire en cours
  const [scoringAll, setScoringAll] = useState(null);       // { done, total } | null
  const isScored = (p) => (p.match_score ?? p.match?.score) != null || (p.match_motif ?? p.match?.motif_non_evaluable) != null;
  const doScore = useCallback(async (propId, { force = false } = {}) => {
    const res = await fetch(`${BASE}/propositions/${propId}/matching${force ? '?force=1' : ''}`, { method: 'POST', headers: jsonH() });
    const j = await res.json().catch(() => ({}));
    if (!res.ok || j.success === false) throw new Error(j.error || `HTTP ${res.status}`);
    const m = j.match || j;
    setProposals(prev => prev.map(p => p.id === propId
      ? { ...p, match_score: m.score ?? null, match_niveau: m.niveau, match_motif: m.motif_non_evaluable || m.motif }
      : p));
  }, []);
  const scoreProposal = useCallback(async (propId, opts) => {
    setScoringPropId(propId);
    try { await doScore(propId, opts); }
    catch (e) { showNotification?.('Erreur matching : ' + e.message, 'error'); }
    finally { setScoringPropId(null); }
  }, [doScore, showNotification]);
  // « Calculer le matching » : ne POSTe que les propositions PAS ENCORE scorées
  // (match_score et match_motif null) — les autres affichent leur score persisté.
  const scoreAllProposals = useCallback(async () => {
    const targets = proposals.filter(p => !isScored(p));
    if (!targets.length) { showNotification?.('Toutes les propositions sont déjà scorées', 'success'); return; }
    setScoringAll({ done: 0, total: targets.length });
    let errors = 0;
    for (const p of targets) {
      try { await doScore(p.id); } catch { errors += 1; }
      setScoringAll(s => (s ? { ...s, done: s.done + 1 } : s));
    }
    setScoringAll(null);
    showNotification?.(errors ? `Matching calculé (${errors} échec${errors > 1 ? 's' : ''})` : 'Matching calculé pour toutes les propositions', errors ? 'error' : 'success');
  }, [proposals, doScore, showNotification]);

  return {
    diffusion, proposals, audiences, loading, saving, busyPropId,
    draftOpen, openDraft: () => setDraftOpen(true), closeDraft: () => setDraftOpen(false),
    publish, unpublish,
    accept: (id) => decide(id, 'accept'),
    reject: (id, reason) => decide(id, 'reject', reason),
    scoreProposal, scoreAllProposals, scoringPropId, scoringAll,
    propCount: proposals.length,
  };
}

// ── Bouton d'en-tête (dossier besoin) ────────────────────────────────────────
export function DiffusionHeaderButton({ diffusion, onOpen }) {
  if (diffusion) {
    return (
      <button onClick={onOpen} className="bd-btn" title="Gérer la diffusion sourceurs"
        style={{ border: '1px solid var(--ok-bd, #bfe3cd)', background: 'var(--ok-bg, #e6f5ec)', color: 'var(--ok-fg, #16794a)' }}>
        <BI icon="check-lg" /> Diffusé{diffusion.audience_count != null ? ` · ${diffusion.audience_count} sourceurs` : ''}
      </button>
    );
  }
  return (
    <button onClick={onOpen} className="bd-btn" title="Diffuser ce besoin à votre réseau de sourceurs freelances"
      style={{ border: 'none', background: 'var(--brand)', color: '#fff' }}>
      <BI icon="send" /> Diffuser aux sourceurs
    </button>
  );
}

// ── Carte de contexte (colonne latérale) ─────────────────────────────────────
export function DiffusionContextCard({ diffusion, propCount, onOpen }) {
  if (!diffusion) return null;
  const row = (label, val, mono) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px' }}>
      <span style={{ color: 'var(--text-2)' }}>{label}</span>
      <span style={{ fontWeight: 600, color: 'var(--text)', fontFamily: mono ? "'IBM Plex Mono',monospace" : 'inherit' }}>{val}</span>
    </div>
  );
  const masked = (diffusion.masked_fields || []).length;
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px', padding: '18px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '13px' }}>
        <SoMark size={26} />
        <span style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)' }}>Diffusion sourceurs</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
        {diffusion.published_at && row('Diffusé', new Date(diffusion.published_at).toLocaleDateString('fr-FR'))}
        {diffusion.audience_count != null && row('Sourceurs notifiés', diffusion.audience_count, true)}
        {row('Champs masqués', `${masked} / 5`, true)}
        {row('Fenêtre de réponse', deadlineLabel(diffusion.response_deadline), true)}
        {row('Propositions reçues', propCount ?? diffusion.proposals_count ?? 0, true)}
      </div>
      <button onClick={onOpen} className="bd-btn" style={{ marginTop: '14px', width: '100%', justifyContent: 'center' }}>Gérer la diffusion</button>
    </div>
  );
}

// ── Modale de diffusion (2 colonnes : contrôles + aperçu temps réel) ──────────
export function DiffusionModal({ besoin, diffusion, audiences, saving, onClose, onPublish, onUnpublish }) {
  const realOf = {
    client: besoinField(besoin, ['client']) || '—',
    salary: suggestSalaryAnnual(besoinField(besoin, ['tarif', 'salaire', 'budget', 'tjm'])) || 'Sur demande',
    location: besoinField(besoin, ['localisation', 'location', 'ville']) || 'Suisse romande',
    start_date: besoinField(besoin, ['date_demarrage', 'date_visee', 'start']) || 'À définir',
    owner: (typeof besoin?.responsable === 'string' ? besoin.responsable : besoin?.responsable?.username) || 'Non communiqué',
  };
  const initMasks = diffusion
    ? MASK_DEFS.reduce((o, m) => ({ ...o, [m.key]: (diffusion.masked_fields || []).includes(m.key) }), {})
    : { ...DEFAULT_MASKS };
  const [masks, setMasks] = useState(initMasks);
  const [audienceId, setAudienceId] = useState(diffusion?.audience_list_id || audiences[0]?.id || 'all');
  // Une diffusion existante porte un response_deadline RÉSOLU (timestamp ISO) —
  // pas une valeur du select. On repart alors sur 7 jours et on montre la date en cours.
  const [deadline, setDeadline] = useState(DEADLINES.some(d => d.value === diffusion?.response_deadline) ? diffusion.response_deadline : '7d');
  const [salaryMaxAnnual, setSalaryMaxAnnual] = useState(diffusion?.salary_max_annual != null ? fmtSalary(diffusion.salary_max_annual) : realOf.salary);

  const title = besoinField(besoin, ['titre', 'poste', 'title']) || 'Besoin';
  const skills = Array.isArray(besoin?.competences) ? besoin.competences
    : (typeof besoin?.competences === 'string' ? besoin.competences.split(/[,;·]/).map(s => s.trim()).filter(Boolean) : []);
  const publicRef = diffusion?.public_ref || (besoin?.id != null ? `SC-${String(besoin.id)}` : 'SC-—');
  const shownVal = (key) => masks[key] ? MASK_DEFS.find(m => m.key === key).masked : (key === 'salary' ? salaryMaxAnnual : realOf[key]);
  const maskedList = MASK_DEFS.filter(m => masks[m.key]);
  const aud = audiences.find(a => a.id === audienceId) || audiences[0];

  const seg = (on, danger) => ({ padding: '7px 13px', border: 'none', fontFamily: 'inherit', fontSize: '11.5px', fontWeight: 600, cursor: 'pointer',
    background: on ? (danger ? 'var(--brand)' : 'var(--accent)') : 'transparent',
    color: on ? (danger ? '#fff' : 'var(--accent-fg)') : 'var(--text-3)' });

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(5,6,8,0.62)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 'min(1060px, 96vw)', maxHeight: '92vh', overflow: 'auto', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '16px' }}>
        {/* En-tête */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '20px 24px', borderBottom: '1px solid var(--hair)' }}>
          <SoMark size={36} />
          <div>
            <h2 style={{ margin: 0, fontFamily: "'Archivo', sans-serif", fontSize: '21px', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-strong)' }}>Diffuser aux sourceurs<span style={{ color: 'var(--brand)' }}>.</span></h2>
            <div style={{ marginTop: '3px', fontSize: '12.5px', color: 'var(--text-2)' }}>{publicRef} · choisissez ce qui est visible sur sourcia</div>
          </div>
          <span style={{ flex: 1 }} />
          <button onClick={onClose} className="bd-icon-btn" title="Fermer" style={{ border: '1px solid var(--input-border)', background: 'var(--ghost-bg)' }}><BI icon="x-lg" /></button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1.02fr 0.98fr' }}>
          {/* Colonne gauche — contrôles */}
          <div style={{ padding: '22px 24px', borderRight: '1px solid var(--hair)' }}>
            <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)' }}>Salaire maximum annuel (affiché aux sourceurs)</div>
            <input value={salaryMaxAnnual} onChange={e => setSalaryMaxAnnual(e.target.value)} placeholder="ex. 165'000 CHF/an"
              style={{ marginTop: '10px', width: '100%', height: '40px', border: '1px solid var(--input-border)', borderRadius: '8px', background: 'var(--input-bg)', padding: '0 12px', fontFamily: 'inherit', fontSize: '13px', color: 'var(--text)', outline: 'none', boxSizing: 'border-box' }} />
            <div style={{ marginTop: '5px', fontSize: '11px', color: 'var(--text-faint)' }}>Salaire annuel — jamais le TJM. Pré-rempli d'après le tarif du besoin.</div>

            <div style={{ marginTop: '20px', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)' }}>Visibilité des informations</div>
            <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {MASK_DEFS.map(m => {
                const real = m.key === 'salary' ? salaryMaxAnnual : realOf[m.key];
                return (
                  <div key={m.key} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '11px 13px', border: '1px solid var(--border)', borderRadius: '10px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text-strong)' }}>{m.label}</div>
                      <div style={{ marginTop: '2px', fontSize: '12px', color: 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{masks[m.key] ? `${real}  →  ${m.masked}` : real}</div>
                    </div>
                    <div style={{ display: 'flex', border: '1px solid var(--input-border)', borderRadius: '8px', overflow: 'hidden', flex: 'none' }}>
                      <button onClick={() => setMasks(v => ({ ...v, [m.key]: false }))} style={seg(!masks[m.key], false)}>Publier</button>
                      <button onClick={() => setMasks(v => ({ ...v, [m.key]: true }))} style={seg(!!masks[m.key], true)}>Masquer</button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop: '20px', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)' }}>Liste de diffusion</div>
            <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '7px' }}>
              {audiences.map(a => {
                const active = a.id === audienceId;
                return (
                  <button key={a.id} onClick={() => setAudienceId(a.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '10px 13px', borderRadius: '9px', border: `1px solid ${active ? 'var(--text-faint)' : 'var(--border)'}`, background: active ? 'var(--accent-soft-bg, var(--ghost-bg))' : 'var(--ghost-bg)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer' }}>
                    <span style={{ width: '14px', height: '14px', borderRadius: '50%', border: active ? '4.5px solid var(--accent)' : '1.5px solid var(--input-border)', background: 'transparent', flex: 'none', boxSizing: 'border-box' }} />
                    <span style={{ flex: 1, textAlign: 'left' }}>{a.name}</span>
                    {a.count != null && <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11.5px', color: 'var(--text-3)' }}>{a.count}</span>}
                  </button>
                );
              })}
            </div>

            <div style={{ marginTop: '20px', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)' }}>Fenêtre de réponse</div>
            <ErpiaSelect value={deadline} onChange={e => setDeadline(e.target.value)}
              style={{ marginTop: '10px', width: '100%', height: '42px', border: '1px solid var(--input-border)', borderRadius: '9px', background: 'var(--input-bg)', padding: '0 13px', fontFamily: 'inherit', fontSize: '13px', color: 'var(--text)', cursor: 'pointer', outline: 'none' }}>
              {DEADLINES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
            </ErpiaSelect>
            {diffusion?.response_deadline && !DEADLINES.some(d => d.value === diffusion.response_deadline) && (
              <div style={{ marginTop: '5px', fontSize: '11px', color: 'var(--text-faint)' }}>Clôture actuelle : {deadlineLabel(diffusion.response_deadline)} — mettre à jour repart de la durée choisie.</div>
            )}
          </div>

          {/* Colonne droite — aperçu temps réel (toujours rendu clair) */}
          <div style={{ padding: '22px 24px', background: 'var(--bg)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)' }}>Aperçu côté sourceur</span>
              <span style={{ fontSize: '10.5px', fontWeight: 600, padding: '2px 7px', borderRadius: '5px', background: 'var(--code-bg)', color: 'var(--text-3)' }}>temps réel</span>
            </div>
            <div style={{ marginTop: '12px', background: '#fff', border: '1px solid #e5e7ec', borderRadius: '13px', padding: '20px 22px', color: '#1a1c24' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ width: '26px', height: '26px', borderRadius: '6px', background: '#0b0c11', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', position: 'relative', flex: 'none' }}>
                  <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '10.5px', fontWeight: 600, color: '#fff' }}>so</span>
                  <span style={{ position: 'absolute', bottom: '3px', right: '3px', width: '3px', height: '3px', borderRadius: '1px', background: '#E2231A' }} />
                </span>
                <span style={{ fontSize: '11px', fontWeight: 600, color: '#878d99' }}>sourcia · nouveau besoin</span>
                <span style={{ flex: 1 }} />
                <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '10.5px', color: '#9aa0ac' }}>{publicRef}</span>
              </div>
              <div style={{ marginTop: '13px', fontFamily: "'Archivo', sans-serif", fontSize: '19px', fontWeight: 800, letterSpacing: '-0.02em', color: '#0b0c11' }}>{title}</div>
              <div style={{ marginTop: '4px', fontSize: '12.5px', color: '#5f656f' }}>{shownVal('client')}</div>
              <div style={{ marginTop: '13px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11.5px', fontWeight: 600, padding: '4px 9px', borderRadius: '6px', background: '#f1f2f5', color: '#4f5663' }}><BI icon="geo-alt" /> {shownVal('location')}</span>
                <span style={{ fontSize: '11.5px', fontWeight: 600, padding: '4px 9px', borderRadius: '6px', background: '#f1f2f5', color: '#4f5663' }}>Démarrage {shownVal('start_date')}</span>
                <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11.5px', fontWeight: 600, padding: '4px 9px', borderRadius: '6px', background: '#f1f2f5', color: '#0b0c11' }}>{shownVal('salary')}</span>
              </div>
              {skills.length > 0 && (
                <div style={{ marginTop: '13px', display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                  {skills.slice(0, 8).map((sk, i) => <span key={i} style={{ fontSize: '11px', fontWeight: 500, padding: '3px 8px', borderRadius: '6px', border: '1px solid #e5e7ec', color: '#5f656f' }}>{sk}</span>)}
                </div>
              )}
              <div style={{ marginTop: '14px', paddingTop: '13px', borderTop: '1px solid #edeff2', display: 'flex', alignItems: 'center', gap: '7px', fontSize: '11.5px', color: '#878d99' }}>
                <BI icon="lock" />
                <span>{maskedList.length === 0 ? 'Toutes les informations sont publiées.' : `Masqué : ${maskedList.map(m => m.label.toLowerCase()).join(', ')}.`}</span>
              </div>
            </div>

            <div style={{ marginTop: '18px', display: 'flex', gap: '10px' }}>
              <button onClick={onClose} style={{ flex: 1, padding: '12px', borderRadius: '9px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>Annuler</button>
              <button onClick={() => onPublish({ masks, audienceId, deadline, salaryMaxAnnual })} disabled={saving}
                style={{ flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', borderRadius: '9px', border: 'none', background: 'var(--brand)', color: '#fff', fontFamily: 'inherit', fontSize: '13.5px', fontWeight: 600, cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1 }}>
                <BI icon={saving ? 'arrow-repeat' : 'send'} style={saving ? { animation: 'ats-spin .7s linear infinite' } : undefined} />
                {diffusion ? 'Mettre à jour la diffusion' : `Diffuser${aud?.count != null ? ` à ${aud.count} sourceurs` : ' aux sourceurs'}`}
              </button>
            </div>
            {diffusion && (
              <button onClick={onUnpublish} disabled={saving}
                style={{ marginTop: '10px', width: '100%', padding: '8px', borderRadius: '8px', border: 'none', background: 'transparent', color: 'var(--text-3)', fontFamily: 'inherit', fontSize: '12px', fontWeight: 500, cursor: 'pointer' }}>Retirer la diffusion</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Onglet « Propositions sourceurs » ────────────────────────────────────────
const PROP_STATUS = {
  new: { label: 'Nouveau', bg: 'var(--brand)', fg: '#fff' },
  accepted: { label: 'Accepté', bg: 'var(--ok-bg, #e6f5ec)', fg: 'var(--ok-fg, #16794a)' },
  rejected: { label: 'Refusé', bg: 'var(--code-bg)', fg: 'var(--text-3)' },
};
// Normalisation tolérante : la plateforme sourcia parle en libellés FR
// (Envoyé / Accepté / Refusé), le contrat API en new|accepted|rejected. Un
// statut non décidé doit TOUJOURS résoudre à 'new', sinon les boutons
// Accepter/Refuser disparaissent alors que la carte affiche « Nouveau ».
const normPropStatus = (s) => {
  const x = String(s || '').toLowerCase();
  if (/accept/.test(x)) return 'accepted';
  if (/refus|reject/.test(x)) return 'rejected';
  return 'new'; // new, nouveau, pending, envoyé/sent, vide…
};

// Chip de score matching — même barème de couleurs que la carte besoin.
// GET /propositions renvoie TOUJOURS match_score/match_motif (null si pas
// encore scoré) : « pas scoré » (les deux null) = aucun chip ; « non évaluable »
// (score null + motif) = NE avec le motif backend tel quel en tooltip.
// `onDetail` — « pourquoi ce score ? ». Le même menu que sur les profils du
// besoin, qui manquait ici (demande 22.09) : un chiffre qu'on ne peut pas
// ouvrir se discute de mémoire, et c'est exactement ce que la décomposition
// critère par critère existe pour éviter.
const MatchChip = ({ score, niveau, motif, niveauFit, niveauEcart, niveauEcartDetail, onDetail }) => {
  if (score == null) {
    return (motif != null && motif !== '')
      ? <span title={motif} style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11px', fontWeight: 600, padding: '3px 8px', borderRadius: '6px', background: 'var(--chip-bg)', color: 'var(--text-3)' }}>NE</span>
      : null;
  }
  const v = Math.round(Number(score));
  const fg = v >= 75 ? '#16a34a' : v >= 50 ? '#d97706' : 'var(--brand)';
  const bg = v >= 75 ? 'rgba(22,163,74,0.12)' : v >= 50 ? 'rgba(217,119,6,0.12)' : 'rgba(226,35,26,0.10)';
  // niveau_fit < 1 (contrat s9) : écart de séniorité à QUALIFIER en entretien,
  // affiché comme réserve à côté du score — pas un rejet. Absent = rien.
  const reserve = niveauFit != null && Number(niveauFit) < 1 && niveauEcart;
  const pastille = { display: 'inline-flex', alignItems: 'center', gap: '5px', fontFamily: "'IBM Plex Mono',monospace",
    fontSize: '11px', fontWeight: 600, padding: '3px 8px', borderRadius: '6px', background: bg, color: fg };
  return <>
    {onDetail
      ? <button type="button" onClick={onDetail}
          title={`Score matching : ${v}/100${niveau ? ' · ' + niveau : ''} — voir le détail critère par critère`}
          style={{ ...pastille, border: 'none', cursor: 'pointer', font: 'inherit', fontFamily: "'IBM Plex Mono',monospace", fontSize: '11px', fontWeight: 600 }}>
          <BI icon="intersect" /> {v}
        </button>
      : <span title={`Score matching : ${v}/100${niveau ? ' · ' + niveau : ''}`} style={pastille}><BI icon="intersect" /> {v}</span>}
    {reserve && (
      <span title={niveauEcartDetail || "Réserve de niveau — à qualifier en entretien, ce n'est pas un rejet"}
        style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 600, padding: '3px 8px', borderRadius: '6px', background: 'var(--erp-warning-soft)', color: 'var(--erp-warning)' }}>
        <BI icon="question-circle" /> {niveauEcart}
      </span>
    )}
  </>;
};

function ProposalCard({ p, busy, scoring, onAccept, onReject, onScore, onDetailScore }) {
  // Aperçu du CV : un cadre isolé, ouvert au-dessus de la liste. On ne quitte
  // pas l'écran pour lire un CV, et le fichier ne s'exécute pas chez nous.
  const [apercuCv, setApercuCv] = useState(null);   // { url, nom } | null
  const c = p.candidate || p;
  const name = c.full_name || c.fullName || p.name || 'Profil';
  const initials = name.split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();
  const status = normPropStatus(p.status);
  const st = PROP_STATUS[status];
  const exp = (c.experience_years ?? c.experienceYears);
  // Sourcia publie désormais une VRAIE date de disponibilité en plus du texte
  // libre : la date prime (précise), le texte reste le repli. Champ absent =
  // ancienne proposition, on n'affiche rien de plus.
  const dispoDate = c.availability_date ?? c.date_disponibilite ?? c.disponibilite_date
    ?? p.date_disponibilite ?? p.availability_date ?? p.disponibilite_date ?? null;
  const dispoChip = (() => {
    const t = dispoDate ? Date.parse(dispoDate) : NaN;
    if (!Number.isNaN(t)) return `dispo le ${new Date(t).toLocaleDateString('fr-FR')}`;
    // Texte libre : la forme publiée par sourcia (disponibilite_texte) ou
    // l'ancienne (availability). Jamais deviné à partir d'autre chose.
    const libre = c.disponibilite_texte ?? p.disponibilite_texte ?? c.availability ?? null;
    return libre ? `dispo ${libre}` : null;
  })();
  // CR de l'entretien mené par le sourceur — publié par sourcia avec la
  // proposition ; absent sur les anciennes propositions.
  const crEntretien = p.cr_entretien ?? p.compte_rendu_entretien ?? p.interview_report
    ?? c.cr_entretien ?? c.compte_rendu_entretien ?? null;
  const chips = [
    exp != null ? `${exp} ans` : null,
    c.location || null,
    (c.salary_expectation || c.salaryExpectation) ? `Salaire ${c.salary_expectation || c.salaryExpectation}` : null,
    dispoChip,
  ].filter(Boolean);
  // Le CV s'ouvre dans un <a> ou une <iframe> : pas d'en-tête possible, le
  // jeton passe en query (la route l'accepte).
  const cvTelecharger = urlAvecJeton(p.cv_download_url || null);
  const cvApercu = urlAvecJeton(p.cv_preview_url || null);
  const cvName = p.cv_file_name || p.cvName || ((cvTelecharger || cvApercu || p.cv_url) ? 'CV joint' : null);
  const at = p.created_at ? new Date(p.created_at).toLocaleDateString('fr-FR') : (p.at || '');
  const srcInitials = (p.sourceur_name || p.sourceur || '').split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '13px', padding: '16px 18px' }}>
        <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'var(--accent-soft-bg, var(--chip-bg))', color: 'var(--text-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 700, flex: 'none' }}>{initials}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '15px', fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--text-strong)' }}>{name}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 9px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap', background: st.bg, color: st.fg }}>{st.label}</span>
            <MatchChip score={p.match_score ?? p.match?.score} niveau={p.match_niveau ?? p.match?.niveau} motif={p.match_motif ?? p.match?.motif_non_evaluable}
              niveauFit={p.match_niveau_fit ?? p.match?.niveau_fit} niveauEcart={p.match_niveau_ecart ?? p.match?.niveau_ecart}
              niveauEcartDetail={p.match_niveau_ecart_detail ?? p.match?.niveau_ecart_detail}
              onDetail={onDetailScore ? () => onDetailScore(p) : null} />
            <span style={{ flex: 1 }} />
            {(() => {
              const scored = (p.match_score ?? p.match?.score) != null || (p.match_motif ?? p.match?.motif_non_evaluable) != null;
              return (
                <button onClick={() => onScore?.(p.id, scored ? { force: true } : undefined)} disabled={scoring}
                  title={scored ? 'Forcer un nouveau calcul du score (ignore le cache)' : 'Calculer le score de matching du CV proposé sur ce besoin (moteur s9)'}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '5px 10px', borderRadius: '7px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text-2)', fontFamily: 'inherit', fontSize: '11.5px', fontWeight: 600, cursor: scoring ? 'wait' : 'pointer', opacity: scoring ? 0.6 : 1, flex: 'none' }}>
                  <BI icon={scoring ? 'arrow-repeat' : 'stars'} style={scoring ? { animation: 'ats-spin .8s linear infinite' } : undefined} /> {scoring ? 'Scoring…' : scored ? 'Re-scorer' : 'Scorer'}
                </button>
              );
            })()}
          </div>
          {(c.headline) && <div style={{ marginTop: '3px', fontSize: '12.5px', color: 'var(--text-2)' }}>{c.headline}</div>}
          {chips.length > 0 && (
            <div style={{ marginTop: '9px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {chips.map((ch, i) => <span key={i} style={{ fontFamily: /Salaire/.test(ch) ? "'IBM Plex Mono',monospace" : 'inherit', fontSize: '11.5px', fontWeight: 600, padding: '3px 9px', borderRadius: '6px', background: 'var(--chip-bg)', color: 'var(--chip-fg, var(--text-2))' }}>{ch}</span>)}
            </div>
          )}
          {p.note && <div style={{ marginTop: '11px', padding: '11px 13px', borderLeft: '2px solid var(--input-border)', background: 'var(--ghost-bg)', borderRadius: '0 8px 8px 0', fontSize: '12.5px', lineHeight: 1.55, color: 'var(--text-2)', fontStyle: 'italic', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>« {p.note} »</div>}
          {crEntretien && (
            <div style={{ marginTop: '11px', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
              <div className="ats-mono" style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '6px 12px', background: 'var(--ghost-bg)', borderBottom: '1px solid var(--hair)', fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-3)' }}>
                <BI icon="chat-left-text" /> CR d'entretien du sourceur
              </div>
              <div style={{ padding: '10px 13px', fontSize: '12.5px', lineHeight: 1.6, color: 'var(--text-2)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{crEntretien}</div>
            </div>
          )}
          {/* Traçabilité de la décision : qui, quand, et la raison pour un refus */}
          {(status === 'rejected' || status === 'accepted') && (() => {
            const who = p.decided_by_name ?? p.rejected_by_name ?? p.accepted_by_name ?? null;
            const whenRaw = p.decided_at ?? p.rejected_at ?? p.accepted_at ?? null;
            const when = whenRaw ? new Date(whenRaw) : null;
            const dateTxt = when && !Number.isNaN(when.getTime()) ? when.toLocaleDateString('fr-FR') : null;
            const reason = status === 'rejected' ? (p.rejection_reason || p.reject_reason) : null;
            if (!who && !dateTxt && !reason) return null;
            return (
              <div style={{ marginTop: '8px', display: 'flex', alignItems: 'flex-start', gap: '6px', fontSize: '12px', color: 'var(--text-3)' }}>
                <BI icon={status === 'rejected' ? 'x-circle' : 'check-circle'} style={{ marginTop: '2px', flex: 'none' }} />
                <span>{status === 'rejected' ? 'Refusée' : 'Acceptée'}{who ? ` par ${who}` : ''}{dateTxt ? ` le ${dateTxt}` : ''}{reason ? ` : ${reason}` : ''}</span>
              </div>
            );
          })()}
          <div style={{ marginTop: '11px', display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}>
            {/* Le CV vient d'un tiers. Le téléchargement passe par l'URL que
                le serveur force en pièce jointe, et l'aperçu par celle qui
                rend un PDF — dans un cadre isolé, jamais dans l'onglet. */}
            {cvName && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}>
                {cvApercu && (
                  <button type="button" onClick={() => setApercuCv({ url: cvApercu, nom: cvName })}
                    title="Voir le CV sans quitter l'écran"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '5px 10px', border: '1px solid var(--border)', borderRadius: '7px', background: 'transparent', fontSize: '11.5px', fontWeight: 600, color: 'var(--text-2)', cursor: 'pointer', fontFamily: 'inherit' }}>
                    <BI icon="eye" /> {cvName}
                  </button>
                )}
                {cvTelecharger
                  ? <a href={cvTelecharger} target="_blank" rel="noopener noreferrer" title="Télécharger le CV"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '5px 10px', border: '1px solid var(--border)', borderRadius: '7px', fontSize: '11.5px', fontWeight: 600, color: 'var(--text-2)', textDecoration: 'none' }}><BI icon="download" />{cvApercu ? '' : ` ${cvName}`}</a>
                  : (!cvApercu && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '5px 10px', border: '1px solid var(--border)', borderRadius: '7px', fontSize: '11.5px', fontWeight: 600, color: 'var(--text-2)' }}><BI icon="file-earmark-text" /> {cvName}</span>)}
              </span>
            )}
            <span style={{ flex: 1 }} />
            {(p.sourceur_name || p.sourceur) && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontSize: '11.5px', color: 'var(--text-3)' }}>
                <span style={{ width: '20px', height: '20px', borderRadius: '50%', background: 'var(--accent-soft-bg, var(--chip-bg))', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: 700, color: 'var(--text-strong)' }}>{srcInitials}</span>
                proposé par <strong style={{ color: 'var(--text-2)' }}>{p.sourceur_name || p.sourceur}</strong>{(p.sourceur_company || p.agency) ? ` · ${p.sourceur_company || p.agency}` : ''}{at ? ` · ${at}` : ''}
              </span>
            )}
          </div>
        </div>
      </div>
      {apercuCv && (
        <div role="dialog" aria-label={`Aperçu de ${apercuCv.nom}`} onClick={() => setApercuCv(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 90, background: 'rgba(8,9,12,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '28px' }}>
          <div onClick={e => e.stopPropagation()} style={{ width: 'min(940px, 96vw)', height: 'min(88vh, 100%)', display: 'flex', flexDirection: 'column', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 15px', borderBottom: '1px solid var(--hair)' }}>
              <BI icon="file-earmark-text" />
              <span style={{ flex: 1, minWidth: 0, fontSize: '13px', fontWeight: 700, color: 'var(--text-strong)' }}>{apercuCv.nom}</span>
              {cvTelecharger && <a href={cvTelecharger} target="_blank" rel="noopener noreferrer" className="erp-btn erp-btn--secondary erp-btn--sm" style={{ textDecoration: 'none' }}><BI icon="download" /> Télécharger</a>}
              <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => setApercuCv(null)}>Fermer</button>
            </div>
            <iframe /* pas de sandbox : cf. DocumentViewer */src={apercuCv.url} title={`CV — ${apercuCv.nom}`} style={{ flex: 1, width: '100%', border: 'none', background: 'var(--surface)' }} />
          </div>
        </div>
      )}
      {status === 'new' && (
        <div style={{ display: 'flex', gap: '9px', padding: '12px 18px', borderTop: '1px solid var(--hair)', alignItems: 'center' }}>
          <button onClick={() => onAccept(p.id)} disabled={busy}
            style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '8px 14px', borderRadius: '8px', border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1 }}>
            <BI icon="check-lg" /> Accepter → Candidats
          </button>
          <button onClick={() => onReject(p)} disabled={busy}
            style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '8px 14px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text-2)', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1 }}>
            <BI icon="x-lg" /> Refuser
          </button>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: '11.5px', color: 'var(--text-3)' }}>Le sourceur sera notifié de votre décision</span>
        </div>
      )}
    </div>
  );
}

// Modale de refus : raison OPTIONNELLE transmise au sourceur avec la notification.
function RejectModal({ proposal, busy, onConfirm, onClose }) {
  const [reason, setReason] = useState('');
  const name = proposal.candidate?.full_name || proposal.candidate?.fullName || proposal.name || 'ce profil';
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 700, background: 'rgba(5,6,8,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 'min(460px, 96vw)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '14px', overflow: 'hidden' }}>
        <div style={{ padding: '18px 20px 0' }}>
          <div style={{ fontSize: '15.5px', fontWeight: 700, color: 'var(--text-strong)' }}>Refuser la proposition</div>
          <div style={{ marginTop: '3px', fontSize: '12.5px', color: 'var(--text-2)' }}>{name} — le sourceur sera notifié de votre décision.</div>
        </div>
        <div style={{ padding: '14px 20px 4px' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: '7px' }}>Raison du refus <span style={{ textTransform: 'none', fontWeight: 500, color: 'var(--text-faint)', letterSpacing: 0 }}>· optionnelle, transmise au sourceur</span></div>
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} autoFocus maxLength={500}
            placeholder="Ex. profil trop junior pour ce poste, TJM hors budget…"
            style={{ width: '100%', boxSizing: 'border-box', border: '1px solid var(--input-border)', borderRadius: '9px', background: 'var(--input-bg)', padding: '10px 12px', fontFamily: 'inherit', fontSize: '13px', color: 'var(--text)', outline: 'none', resize: 'vertical' }} />
          {reason.length >= 450 && <div style={{ marginTop: '4px', fontSize: '11px', color: 'var(--text-faint)', textAlign: 'right', fontFamily: "'IBM Plex Mono',monospace" }}>{reason.length}/500</div>}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '9px', padding: '14px 20px 18px' }}>
          <button onClick={onClose} disabled={busy} style={{ padding: '9px 14px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>Annuler</button>
          <button onClick={() => onConfirm(reason)} disabled={busy}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 16px', borderRadius: '8px', border: 'none', background: 'var(--brand)', color: '#fff', fontFamily: 'inherit', fontSize: '13px', fontWeight: 600, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1 }}>
            <BI icon={busy ? 'arrow-repeat' : 'x-lg'} style={busy ? { animation: 'ats-spin .7s linear infinite' } : undefined} /> Refuser
          </button>
        </div>
      </div>
    </div>
  );
}

export function PropositionsTab({ diffusion, proposals, audienceCount, busyPropId, onOpenDiff, onAccept, onReject, onScore, onScoreAll, scoringPropId, scoringAll }) {
  const [rejectFor, setRejectFor] = useState(null); // proposition en cours de refus (modale raison)
  const [detailScore, setDetailScore] = useState(null); // « pourquoi ce score ? » sur une proposition
  if (!diffusion && proposals.length === 0) {
    return (
      <div style={{ padding: '56px 20px', textAlign: 'center', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px' }}>
        <div style={{ width: '46px', height: '46px', borderRadius: '12px', background: 'var(--chip-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', color: 'var(--text-faint)' }}><BI icon="send" /></div>
        <p style={{ margin: 0, fontSize: '14.5px', fontWeight: 600, color: 'var(--text)' }}>Ce besoin n'est pas encore diffusé</p>
        <p style={{ margin: '6px 0 0', fontSize: '13px', color: 'var(--text-2)' }}>Diffusez-le à votre réseau de sourceurs pour recevoir des propositions de profils.</p>
        <button onClick={onOpenDiff} style={{ marginTop: '16px', display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 14px', borderRadius: '8px', border: 'none', background: 'var(--brand)', color: '#fff', fontFamily: 'inherit', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}><BI icon="send" /> Diffuser aux sourceurs</button>
      </div>
    );
  }
  if (diffusion && proposals.length === 0) {
    return (
      <div style={{ padding: '56px 20px', textAlign: 'center', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px' }}>
        <div style={{ width: '46px', height: '46px', borderRadius: '12px', background: 'var(--chip-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', color: 'var(--text-faint)' }}><BI icon="clock-history" /></div>
        <p style={{ margin: 0, fontSize: '14.5px', fontWeight: 600, color: 'var(--text)' }}>En attente de propositions</p>
        <p style={{ margin: '6px 0 0', fontSize: '13px', color: 'var(--text-2)' }}>{audienceCount != null ? `${audienceCount} sourceurs notifiés · ` : ''}fenêtre de réponse {deadlineLabel(diffusion.response_deadline)}</p>
      </div>
    );
  }
  const scoringBusy = scoringAll != null || scoringPropId != null;
  // Le compteur du bouton global n'annonce que ce qui sera réellement calculé
  // (les propositions déjà scorées — score persisté par le back — sont sautées).
  const unscored = proposals.filter(p => (p.match_score ?? p.match?.score) == null && (p.match_motif ?? p.match?.motif_non_evaluable) == null).length;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {/* Barre d'actions de la liste : matching de toutes les propositions d'un coup */}
      {proposals.length > 1 && unscored > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onScoreAll} disabled={scoringBusy}
            title="Calculer le score de matching de chaque profil pas encore scoré (moteur s9)"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '8px 13px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, cursor: scoringBusy ? 'wait' : 'pointer', opacity: scoringBusy ? 0.6 : 1 }}>
            <BI icon={scoringAll ? 'arrow-repeat' : 'stars'} style={scoringAll ? { animation: 'ats-spin .8s linear infinite' } : undefined} />
            {scoringAll
              ? <span style={{ fontVariantNumeric: 'tabular-nums' }}>Scoring {scoringAll.done}/{scoringAll.total}</span>
              : `Calculer le matching (${unscored})`}
          </button>
        </div>
      )}
      {/* « Pourquoi ce score ? » — la MÊME modale que sur les profils du besoin,
          nourrie par la proposition (le profil n'a pas encore de fiche). */}
      {detailScore && (
        <ScoreDetailModal
          chemin={`${BASE}/propositions/${detailScore.id}/score-detail`}
          nom={detailScore.candidate_full_name || detailScore.candidate?.full_name || null}
          onClose={() => setDetailScore(null)} />
      )}
      {proposals.map(p => <ProposalCard key={p.id} p={p} busy={busyPropId === p.id}
        scoring={scoringPropId === p.id || scoringAll != null}
        onAccept={onAccept} onReject={setRejectFor} onScore={onScore}
        onDetailScore={setDetailScore} />)}

      {rejectFor && (
        <RejectModal proposal={rejectFor} busy={busyPropId === rejectFor.id}
          onClose={() => setRejectFor(null)}
          onConfirm={(reason) => { onReject(rejectFor.id, reason); setRejectFor(null); }} />
      )}
    </div>
  );
}
